import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Trash2, Save, Download } from 'lucide-react';
import { BTN_ON_DARK, BTN_ON_LIGHT, TEXT_ON_DARK } from '../../../styles/tokens';
import type { GalleryItem } from '../../../services/api';
import { deleteGalleryItems, saveStampedImage } from '../../../services/api';
import { getProxyUrl } from '../utils/proxyUrl';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useToast } from '../../../hooks/useToast';
import { useImageBlobPrefetch, fetchImageBlob } from '../../../hooks/useImageBlobPrefetch';
import { saveImage, canSaveToPhotos } from '../../../utils/saveImage';
import { useStamps } from '../hooks/useStamps';
import { useDrawCanvas } from '../hooks/useDrawCanvas';
import { drawShapeOnCanvas } from '../utils/stampShapes';
import StampToolbar from './StampToolbar';
import StampOverlay from './StampOverlay';
import DrawCanvas from './DrawCanvas';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { RunningLoader } from '@/components/RunningLoader';

const TYPE_LABEL: Record<string, string> = {
  generated: 'AI 快速生成',
  'style-transfer': '人像風格',
  agent: '協作聊天',
  upload: '上傳',
  'remove-bg': '去背',
  'stamp-edit': '使用者編輯',
};

interface GalleryLightboxProps {
  item: GalleryItem;
  hasPrev: boolean;
  hasNext: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDeleted: (id: string) => void;
  onSaved: (newItem: GalleryItem) => void;
}

const GalleryLightbox: React.FC<GalleryLightboxProps> = ({
  item,
  hasPrev,
  hasNext,
  onClose,
  onPrev,
  onNext,
  onDeleted,
  onSaved,
}) => {
  const { isDark } = useDarkMode();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  // 圖片顯示期間就把原圖備妥。切下一張時 hook 會作廢舊 blob，
  // 所以連續切圖後馬上按儲存，拿到的一定是當前這張
  const takeOriginalBlob = useImageBlobPrefetch(item.imageUrl);
  const actionBtn = isDark ? BTN_ON_DARK : BTN_ON_LIGHT;
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const { stamps, addStamp, updateStamp, removeStamp, clearStamps } = useStamps();
  const drawCanvas = useDrawCanvas();
  const eyedropperCallbackRef = useRef<((color: string) => void) | null>(null);
  const [isPaintMode, setIsPaintMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 切換圖片時清除印章與畫盤，並重設 canvas 尺寸（讓下次進入畫盤時重新同步）
  useEffect(() => {
    clearStamps();
    drawCanvas.clearAll();
    if (drawCanvas.canvasRef.current) {
      drawCanvas.canvasRef.current.width = 0;
      drawCanvas.canvasRef.current.height = 0;
    }
    setIsPaintMode(false);
  }, [item.id]);

  // 進入畫盤模式時同步 canvas 尺寸（canvas.width > 0 表示已有筆跡，保留不重設）
  useEffect(() => {
    if (!isPaintMode || !imageContainerRef.current || !drawCanvas.canvasRef.current) return;
    const canvas = drawCanvas.canvasRef.current;
    if (canvas.width > 0) return;
    const container = imageContainerRef.current;
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
  }, [isPaintMode]);

  // 鍵盤導航
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPrev, hasNext, onClose, onPrev, onNext]);

  // 合成原圖 + 筆跡 + 所有印章為單一 blob；供儲存與另存新檔共用
  const composeImage = async (): Promise<Blob> => {
    // 優先用預抓好的原圖，省掉一次網路往返 —— 合成愈快，
    // iOS 的 user activation 就愈有機會撐到 navigator.share() 被呼叫
    const blob = takeOriginalBlob() ?? await fetchImageBlob(item.imageUrl);
    const imgBitmap = await createImageBitmap(blob);

    const canvas = document.createElement('canvas');
    canvas.width = imgBitmap.width;
    canvas.height = imgBitmap.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(imgBitmap, 0, 0);

    // 合成畫布層（筆跡）— 只在實際畫過筆跡時才合成，避免 0×0 canvas 導致 drawImage 失敗
    const drawingCanvas = drawCanvas.canvasRef.current;
    if (drawingCanvas && drawCanvas.historyLength > 0 && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(drawingCanvas, 0, 0, imgBitmap.width, imgBitmap.height);
    }

    const container = imageContainerRef.current;
    const containerW = container?.offsetWidth ?? imgBitmap.width;
    const containerH = container?.offsetHeight ?? imgBitmap.height;
    const scaleX = imgBitmap.width / containerW;
    const scaleY = imgBitmap.height / containerH;

    for (const stamp of stamps) {
      if (!stamp.imageDataUrl) continue;
      const stampPxLeft = (stamp.xPct / 100) * containerW * scaleX;
      const stampPxTop = (stamp.yPct / 100) * containerH * scaleY;
      const stampPxW = (stamp.sizePct / 100) * containerW * scaleX;
      const hasExplicitHeight = stamp.heightPct !== undefined;
      const stampPxHExplicit = hasExplicitHeight ? (stamp.heightPct! / 100) * containerH * scaleY : 0;

      if (stamp.shapeMeta) {
        const { kind, color, lineWidth, filled } = stamp.shapeMeta;
        const h = hasExplicitHeight ? stampPxHExplicit : stampPxW;
        drawShapeOnCanvas(ctx, kind, color, lineWidth, filled, stampPxLeft, stampPxTop, stampPxW, h);
        continue;
      }

      await new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => {
          const stampPxH = hasExplicitHeight
            ? stampPxHExplicit
            : stampPxW * (img.naturalHeight / img.naturalWidth);
          ctx.drawImage(img, stampPxLeft, stampPxTop, stampPxW, stampPxH);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = stamp.imageDataUrl!;
      });
    }

    const resultBlob = await new Promise<Blob | null>(resolve => {
      canvas.toBlob(resolve, 'image/jpeg', 0.95);
    });
    if (!resultBlob) throw new Error('Canvas 轉換失敗');
    return resultBlob;
  };

  const handleSaveAs = async () => {
    setIsSaving(true);
    try {
      const resultBlob = await composeImage();
      const newItem = await saveStampedImage(resultBlob);
      clearStamps();
      onSaved(newItem);
    } catch (error) {
      console.error('另存新檔失敗', error);
    } finally {
      setIsSaving(false);
    }
  };

  // 滴管取色：點擊圖片取得對應像素顏色
  const handleEyedropperClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    if (drawCanvas.tool !== 'eyedropper') return;
    const container = imageContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    try {
      const res = await fetch(getProxyUrl(item.imageUrl), { credentials: 'include' });
      const blob = await res.blob();
      const bitmap = await createImageBitmap(blob);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 1;
      tempCanvas.height = 1;
      const ctx = tempCanvas.getContext('2d')!;
      const scaleX = bitmap.width / container.offsetWidth;
      const scaleY = bitmap.height / container.offsetHeight;
      ctx.drawImage(bitmap, -clickX * scaleX, -clickY * scaleY, bitmap.width, bitmap.height);
      const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      if (eyedropperCallbackRef.current) {
        eyedropperCallbackRef.current(hex);
        eyedropperCallbackRef.current = null;
      } else {
        drawCanvas.setColor(hex);
      }
      drawCanvas.setTool('brush');
    } catch {
      console.error('滴管取色失敗');
    }
  }, [drawCanvas, item.imageUrl]);

  const handleRequestEyedropper = useCallback((callback: (color: string) => void) => {
    eyedropperCallbackRef.current = callback;
    drawCanvas.setTool('eyedropper');
  }, [drawCanvas]);

  /** 儲存目前這張圖。iOS 走系統分享表單（可存進照片庫），其餘平台直接下載 */
  const handleSave = async () => {
    try {
      const hasEdits = stamps.length > 0 || drawCanvas.historyLength > 0;
      const blob = hasEdits
        ? await composeImage()                                        // 含印章 / 筆跡
        : (takeOriginalBlob() ?? await fetchImageBlob(item.imageUrl)); // 原圖，預抓命中時同步取得
      await saveImage(blob, `jodoni-${item.id}${hasEdits ? '-edited' : ''}.jpg`, '里務宣傳圖片');
    } catch (error) {
      console.error('圖片儲存失敗：', error);
      showToast('圖片儲存失敗，請稍後再試');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteGalleryItems([item.id]);
      onDeleted(item.id);
    } catch (error) {
      console.error('刪除失敗', error);
      setIsDeleting(false);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <>
    {/* 另存新檔處理中：全螢幕遮罩＋loading 動畫，避免合成/上傳時畫面像當機沒有回饋 */}
    {isSaving && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm text-white">
        <RunningLoader size="lg" label="圖片處理中，請稍候" />
      </div>
    )}
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      {/* 關閉按鈕 */}
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white bg-black/40 rounded-full p-2 transition z-10"
        onClick={onClose}
      >
        <X size={24} />
      </button>

      {/* 上一張 */}
      {hasPrev && (
        <button
          className="absolute left-4 text-white/70 hover:text-white bg-black/40 rounded-full p-2 transition z-10"
          onClick={e => { e.stopPropagation(); onPrev(); }}
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* 下一張 */}
      {hasNext && (
        <button
          className="absolute right-4 text-white/70 hover:text-white bg-black/40 rounded-full p-2 transition z-10"
          onClick={e => { e.stopPropagation(); onNext(); }}
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* 主內容區 — 手機改為 flex-col 讓圖片佔滿寬度，toolbar 移到圖片下方 */}
      <div
        className="flex flex-col lg:flex-row gap-2 lg:gap-3 mx-4 lg:mx-16 max-h-[90vh] max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-160px)]"
        onClick={e => e.stopPropagation()}
      >
        {/* StampToolbar：桌面在圖片左側（直向）、手機在圖片下方（橫向） */}
        <div className="flex-shrink-0 order-2 lg:order-1 max-w-full">
          <StampToolbar
            onAddStamp={addStamp}
            onTogglePaint={() => setIsPaintMode(v => !v)}
            isPaintMode={isPaintMode}
            draw={drawCanvas}
            onRequestEyedropper={handleRequestEyedropper}
            orientation={isMobile ? 'horizontal' : 'vertical'}
          />
        </div>

        {/* 右側：圖片 + 操作列（手機時為上方） */}
        <div className="flex flex-col gap-3 min-w-0 order-1 lg:order-2">
          {/* 圖片 + DrawCanvas + StampOverlay */}
          <div
            ref={imageContainerRef}
            className="relative self-start"
            onClick={handleEyedropperClick}
            style={{ cursor: drawCanvas.tool === 'eyedropper' ? 'crosshair' : undefined }}
          >
            <img
              src={item.imageUrl}
              alt=""
              className="block max-w-full max-h-[75vh] rounded-xl shadow-2xl select-none"
              draggable={false}
              style={{
                display: 'block',
                // 觸控裝置：img 不接收 pointer 事件，避免攔截滴管/印章互動，並關閉 iOS 長按預覽
                pointerEvents: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
              }}
            />
            <DrawCanvas
              canvasRef={drawCanvas.canvasRef}
              tool={drawCanvas.tool}
              containerRef={imageContainerRef}
              onStartDrawing={drawCanvas.startDrawing}
              onContinueDrawing={drawCanvas.continueDrawing}
              onEndDrawing={drawCanvas.endDrawing}
              active={isPaintMode}
            />
            <StampOverlay
              stamps={stamps}
              containerRef={imageContainerRef}
              onUpdate={updateStamp}
              onRemove={removeStamp}
              onRequestEyedropper={handleRequestEyedropper}
            />
          </div>

          {/* 底部操作列：手機堆疊，按鈕等寬充滿；桌面維持並排 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3 px-1">
            <div className="text-center md:text-left">
              <span className={`${TEXT_ON_DARK.primary} text-sm font-medium`}>
                {TYPE_LABEL[item.source] ?? item.source}
              </span>
              <span className={`${TEXT_ON_DARK.muted} text-xs ml-3`}>
                {new Date(item.createdAt).toLocaleString('zh-TW')}
              </span>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 text-sm rounded-lg px-3 py-2 md:py-1.5 ${actionBtn.danger}`}
              >
                <Trash2 size={15} />
                刪除
              </button>
              <button
                onClick={() => void handleSave()}
                className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 text-sm rounded-lg px-3 py-2 md:py-1.5 ${actionBtn.neutral}`}
              >
                <Download size={15} />
                {canSaveToPhotos() ? '儲存' : '下載'}
              </button>
              <button
                onClick={handleSaveAs}
                disabled={(stamps.length === 0 && drawCanvas.historyLength === 0) || isSaving}
                title={
                  stamps.length === 0 && drawCanvas.historyLength === 0
                    ? '請先加入印章、文字或畫筆筆跡'
                    : '另存新檔'
                }
                className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 text-sm rounded-lg px-3 py-2 md:py-1.5 ${actionBtn.primary}`}
              >
                <Save size={15} />
                {isSaving ? '儲存中…' : '另存新檔'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {showDeleteConfirm && (
      <ConfirmDialog
        title="確認刪除圖片"
        message="此圖片將移至垃圾桶，確定要刪除嗎？"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isDeleting}
      />
    )}
    </>
  );
};

export default GalleryLightbox;
